import { z } from 'zod';

/**
 * Structure Template — a reference écriture (DOCX or PDF) the lawyer authored
 * previously, parsed through Mistral OCR + Sonnet 4.6 once, and stored per
 * (cabinet, document_type). The extracted `style_block` is a single XML balises
 * blob appended verbatim to Pipeline V2 system prompts so the generated
 * document mimics the cabinet's house style.
 *
 * One row per (cabinet, document_type). Re-upload replaces.
 */

export const StructureTemplateSourceFormatSchema = z.enum(['docx', 'pdf']);
export type StructureTemplateSourceFormat = z.infer<typeof StructureTemplateSourceFormatSchema>;

export const StructureTemplateSchema = z.object({
  id: z.string().uuid(),
  cabinetId: z.string().uuid(),
  documentType: z.string().min(1),
  sourceFilename: z.string().min(1),
  sourceFormat: StructureTemplateSourceFormatSchema,
  styleBlock: z.string().min(1),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StructureTemplate = z.infer<typeof StructureTemplateSchema>;

/**
 * Multipart payload accepted by `POST /api/structure-templates`.
 * The file itself is sent as multer field `file`; this schema validates the
 * accompanying form fields only.
 */
export const CreateStructureTemplateBodySchema = z.object({
  documentType: z.string().min(1, 'documentType is required'),
});
export type CreateStructureTemplateBody = z.infer<typeof CreateStructureTemplateBodySchema>;

export const StructureTemplateLookupQuerySchema = z.object({
  documentType: z.string().min(1, 'documentType is required'),
});
export type StructureTemplateLookupQuery = z.infer<typeof StructureTemplateLookupQuerySchema>;

/** Public-facing response for create / get-by-type / get-by-id. */
export interface StructureTemplateResponse {
  id: string;
  cabinetId: string;
  documentType: string;
  sourceFilename: string;
  sourceFormat: StructureTemplateSourceFormat;
  styleBlock: string;
  createdAt: string;
  updatedAt: string;
}

/** XML balises that frame the extractor output. Used in tests + sanity checks. */
export const STYLE_BLOCK_OUTER_TAG = 'modele_de_reference';
export const STYLE_BLOCK_REQUIRED_INNER_TAGS = [
  'style_de_redaction',
  'structure_de_lacte',
  'consigne_de_mimétisme',
] as const;
